import { useState } from 'react';

/**
 * OptimizedImage Component
 * - Implements lazy loading
 * - WebP format support with fallback
 * - Keyword-rich alt text for SEO
 * - Progressive loading with placeholder
 */
const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  width,
  height,
  priority = false,
  objectFit = 'cover'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Convert image URL to WebP if it's from Pexels or Unsplash
  const getWebPUrl = (url) => {
    if (url.includes('pexels.com')) {
      // Pexels supports auto format
      return url.includes('?') 
        ? `${url}&auto=compress&cs=tinysrgb&fm=webp`
        : `${url}?auto=compress&cs=tinysrgb&fm=webp`;
    }
    if (url.includes('unsplash.com')) {
      // Unsplash supports fm=webp
      return url.includes('?')
        ? `${url}&fm=webp&q=80`
        : `${url}?fm=webp&q=80`;
    }
    // For local images, assume WebP version exists
    if (url.startsWith('/') && !url.includes('.webp')) {
      return url.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    }
    return url;
  };

  const webpSrc = getWebPUrl(src);
  const fallbackSrc = src;

  return (
    <picture className={`relative ${className}`}>
      {/* WebP source for modern browsers */}
      <source srcSet={webpSrc} type="image/webp" />
      
      {/* Fallback for older browsers */}
      <source srcSet={fallbackSrc} type="image/jpeg" />
      
      {/* Actual img element */}
      <img
        src={fallbackSrc}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        className={`${className} transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ objectFit }}
        onLoad={() => setIsLoaded(true)}
        onError={() => {
          setError(true);
          setIsLoaded(true);
        }}
      />
      
      {/* Loading placeholder */}
      {!isLoaded && !error && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
    </picture>
  );
};

export default OptimizedImage;
